import {
  type Booking,
  type FacilityResource,
  hasLevel,
  ladderAccess,
  useAccess,
  useBookings,
  useBookRoom,
  useCampuses,
  useCreateResource,
  useDecideBooking,
  useReleaseBooking,
  useRemoveResource,
  useRescheduleBooking,
  useResources,
  useUpdateResource,
} from '@church/data'
import {
  faBan,
  faBoxArchive,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faDoorOpen,
  faLock,
  faPlus,
  faTrashCan,
  faUsers,
} from '@church/ui-icons'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  Tabs,
  Textarea,
} from '../components/ui.tsx'
import { useTabParam } from '../lib/tabs.ts'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'
import { PageAction } from '../components/PageAction.tsx'

/**
 * Rooms, and who has them when.
 *
 * A clash is a normal outcome here, not an error: the server refuses the second
 * booking for a slot and says which booking has it, so the form shows that
 * sentence and the person can pick another time. Nothing pre-checks and then
 * pretends — the save is what decides.
 *
 * Every list reads at a glance; a booking, a request or a room opens a panel
 * with what can be done to it.
 */
export const Route = createFileRoute('/_authed/_shell/facilities')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'events', 'viewer')) throw redirect({ to: '/' })
  },
  component: FacilitiesScreen,
})

const TABS = ['Bookings', 'Requests', 'Rooms'] as const
type Tab = (typeof TABS)[number]

function FacilitiesScreen() {
  const [tab, setTab] = useTabParam<Tab>(TABS)
  const { data: pending } = useBookings({ status: 'pending' })
  const waiting = pending?.length ?? 0

  return (
    <div className="space-y-6">
      <PageHeader title="Facilities" />
      <Tabs tabs={TABS} value={tab} onChange={setTab}>
        {tab === 'Bookings' && <BookingsPanel />}
        {tab === 'Requests' && <RequestsPanel />}
        {tab === 'Rooms' && <RoomsPanel />}
      </Tabs>
      {waiting > 0 && tab !== 'Requests' && (
        <p className="text-xs text-content-subtle">
          {waiting} request{waiting === 1 ? '' : 's'} waiting for an answer under Requests.
        </p>
      )}
    </div>
  )
}

const dayRange = (day: string) => ({
  from: new Date(`${day}T00:00:00`).toISOString(),
  to: new Date(`${day}T23:59:59`).toISOString(),
})

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })

/** `HH:MM` in local time, for a time input. */
const timeOf = (iso: string) => {
  const date = new Date(iso)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

const shiftDay = (day: string, by: number) => {
  const date = new Date(`${day}T12:00:00`)
  date.setDate(date.getDate() + by)
  return date.toISOString().slice(0, 10)
}

/** What a booking row shows on a phone; every field shows from `sm` up. */
const BOOKING_FIELDS = [
  { id: 'time', label: 'Time', default: true },
  { id: 'purpose', label: 'What for', default: true },
  { id: 'status', label: 'Approval', default: true },
] as const
type BookingField = (typeof BOOKING_FIELDS)[number]['id']

function BookingsPanel() {
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10))
  const [booking, setBooking] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const { data: bookings, isPending } = useBookings(dayRange(day))
  const display = useListDisplay('facility-bookings', BOOKING_FIELDS)
  const shown = bookings?.find((row) => row.id === shownId)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <IconButton
          icon={faChevronLeft}
          label="Previous day"
          onClick={() => setDay((d) => shiftDay(d, -1))}
        />
        <Input
          type="date"
          aria-label="Day"
          className="max-w-44"
          value={day}
          onChange={(event) => setDay(event.target.value)}
        />
        <IconButton
          icon={faChevronRight}
          label="Next day"
          onClick={() => setDay((d) => shiftDay(d, 1))}
        />
        <div className="ml-auto flex items-center gap-1">
          <DisplayOptions fields={BOOKING_FIELDS} shown={display.shown} onChange={display.set} />
          <RequireAccess module="events" level="editor">
            <PageAction icon={faPlus} label="Book a room" onClick={() => setBooking(true)} />
          </RequireAccess>
        </div>
      </div>

      {isPending ? (
        <Spinner />
      ) : !bookings || bookings.length === 0 ? (
        <EmptyState title="Nothing booked that day" />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {bookings.map((row) => (
              <li key={row.id}>
                <BookingRow
                  booking={row}
                  hide={display.hide}
                  onOpen={() => {
                    setShownId(row.id)
                    setOpenId(row.id)
                  }}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <BookRoomForm key={day} day={day} open={booking} onDone={() => setBooking(false)} />
      <BookingPanel
        booking={shown}
        open={openId !== null && shown !== undefined}
        onClose={() => setOpenId(null)}
      />
    </div>
  )
}

function BookingRow({
  booking,
  hide,
  onOpen,
}: {
  booking: Booking
  hide: (field: BookingField) => string
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken sm:gap-4`}
    >
      <span
        className={`w-32 shrink-0 text-sm font-medium tabular-nums text-content-muted ${hide('time')}`}
      >
        {clock(booking.startsAt)}–{clock(booking.endsAt)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-medium text-content-default">
          {booking.resourceName}
        </span>
        <span className={`block truncate text-sm text-content-muted ${hide('purpose')}`}>
          {booking.purpose}
        </span>
      </span>
      {booking.quantity !== null && <Chip tone="brand">×{booking.quantity}</Chip>}
      {booking.status === 'pending' && (
        <span className={hide('status')}>
          <Chip tone="warning">Awaiting approval</Chip>
        </span>
      )}
    </button>
  )
}

/** One booking: what it is, moving it, and giving the room back. */
function BookingPanel({
  booking,
  open,
  onClose,
}: {
  booking: Booking | undefined
  open: boolean
  onClose: () => void
}) {
  const release = useReleaseBooking()
  const access = useAccess()
  if (!booking) return null
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={booking.resourceName}
      description={`${when(booking.startsAt)}–${clock(booking.endsAt)}`}
      footer={
        <ConfirmButton
          label="Release the room"
          icon={faDoorOpen}
          variant="quietDanger"
          className="-ml-4"
          title={`Release ${booking.resourceName}?`}
          description="The room becomes free for that time immediately."
          confirmLabel="Release the room"
          onConfirm={() => release.mutate(booking.id, { onSuccess: onClose })}
        />
      }
    >
      <PanelSection title="What for">
        <p className="text-base text-content-default">{booking.purpose}</p>
        {booking.notes && <p className="text-sm text-content-muted">{booking.notes}</p>}
      </PanelSection>
      {booking.quantity !== null && (
        <PanelSection title="How many">
          <p className="text-base tabular-nums text-content-default">{booking.quantity}</p>
        </PanelSection>
      )}
      <PanelSection title="Approval">
        {booking.status === 'pending' ? (
          <Chip tone="warning" size="lg">
            Awaiting approval
          </Chip>
        ) : (
          <Chip tone="success" size="lg">
            <Icon icon={faCheck} />
            Booked
          </Chip>
        )}
        {booking.decisionNote && (
          <p className="text-sm text-content-muted">“{booking.decisionNote}”</p>
        )}
      </PanelSection>
      {access.has('events', 'editor') && <MoveBooking key={booking.id} booking={booking} />}
      <ErrorNote error={release.error} />
    </SidePanel>
  )
}

function MoveBooking({ booking }: { booking: Booking }) {
  const move = useRescheduleBooking()
  const [day, setDay] = useState(() => booking.startsAt.slice(0, 10))
  const [from, setFrom] = useState(() => timeOf(booking.startsAt))
  const [to, setTo] = useState(() => timeOf(booking.endsAt))
  const [quantity, setQuantity] = useState(() => String(booking.quantity ?? ''))
  const changed =
    from !== timeOf(booking.startsAt) ||
    to !== timeOf(booking.endsAt) ||
    day !== booking.startsAt.slice(0, 10) ||
    (booking.quantity !== null && Number(quantity) !== booking.quantity)

  return (
    <PanelSection
      title={booking.quantity === null ? 'Move it' : 'Move it, or change how many'}
      hint={
        booking.quantity === null
          ? 'If the room is taken then, it says who has it.'
          : 'If there are not enough then, it says how many are free.'
      }
    >
      <Input
        type="date"
        aria-label="Day"
        value={day}
        onChange={(event) => setDay(event.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="time"
          aria-label="From"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <Input
          type="time"
          aria-label="To"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </div>
      {booking.quantity !== null && (
        <Input
          type="number"
          min={1}
          aria-label="How many"
          className="block max-w-32"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
      )}
      <ErrorNote error={move.error} />
      <Button
        variant="secondary"
        disabled={!changed || move.isPending}
        onClick={() =>
          move.mutate({
            id: booking.id,
            startsAt: new Date(`${day}T${from}`).toISOString(),
            endsAt: new Date(`${day}T${to}`).toISOString(),
            ...(booking.quantity === null ? {} : { quantity: Math.max(1, Number(quantity) || 1) }),
          })
        }
      >
        {move.isPending ? 'Moving…' : 'Move booking'}
      </Button>
    </PanelSection>
  )
}

/**
 * The facilities manager's inbox.
 *
 * A pending request already holds the slot, which is why the wording is
 * "waiting for an answer" rather than "requested": nobody else can take that
 * room in the meantime, so leaving it unanswered has a cost.
 */
function RequestsPanel() {
  const { data: pending, isPending } = useBookings({ status: 'pending' })
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const shown = pending?.find((row) => row.id === shownId)

  if (isPending) return <Spinner />
  if (!pending || pending.length === 0) {
    return (
      <EmptyState
        title="Nothing waiting"
        hint="Requests for rooms that need approval land here, and hold the slot until you answer."
      />
    )
  }

  return (
    <>
      <Card bleed>
        <ul className="divide-y divide-border-subtle">
          {pending.map((booking) => (
            <li key={booking.id}>
              <button
                type="button"
                onClick={() => {
                  setShownId(booking.id)
                  setOpenId(booking.id)
                }}
                className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-medium text-content-default">
                    {booking.resourceName}
                  </span>
                  <span className="block truncate text-sm text-content-muted">
                    {booking.purpose} · {when(booking.startsAt)}
                  </span>
                </span>
                <Chip tone="warning">Waiting</Chip>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <RequestPanel
        booking={shown}
        open={openId !== null && shown !== undefined}
        onClose={() => setOpenId(null)}
      />
    </>
  )
}

/** Answering one request: the details, a note back, approve or reject. */
function RequestPanel({
  booking,
  open,
  onClose,
}: {
  booking: Booking | undefined
  open: boolean
  onClose: () => void
}) {
  const decide = useDecideBooking()
  const [note, setNote] = useState('')
  if (!booking) return null
  const answer = (decision: 'approve' | 'reject') =>
    decide.mutate(
      { id: booking.id, decision, note: note.trim() || null },
      {
        onSuccess: () => {
          setNote('')
          onClose()
        },
      },
    )

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={booking.resourceName}
      description={`${when(booking.startsAt)}–${clock(booking.endsAt)}`}
      primary={
        <Button
          variant="success"
          icon={faCheck}
          className="py-1.5"
          pending={decide.isPending}
          onClick={() => answer('approve')}
        >
          Approve
        </Button>
      }
    >
      <PanelSection title="What for">
        <p className="text-base text-content-default">{booking.purpose}</p>
        {booking.notes && <p className="text-sm text-content-muted">{booking.notes}</p>}
      </PanelSection>
      <PanelSection title="A note back" hint="Optional. Whoever asked sees it with your answer.">
        <Textarea
          rows={3}
          aria-label="A note back"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </PanelSection>
      <ErrorNote error={decide.error} />
      <div className="flex flex-wrap gap-2">
        <Button
          variant="danger"
          icon={faBan}
          disabled={decide.isPending}
          onClick={() => answer('reject')}
        >
          Reject
        </Button>
      </div>
      <p className="text-xs text-content-muted">Rejecting frees the room straight away.</p>
    </SidePanel>
  )
}

function BookRoomForm({ day, open, onDone }: { day: string; open: boolean; onDone: () => void }) {
  const book = useBookRoom()
  const { data: resources } = useResources()
  const blank = {
    resourceId: '',
    purpose: '',
    day,
    startTime: '19:00',
    endTime: '21:00',
    notes: '',
    quantity: '1',
  }
  const [form, setForm] = useState(blank)

  const chosen = (resources ?? []).find((resource) => resource.id === form.resourceId)
  const equipment = chosen?.kind === 'equipment'

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone()
      }}
      title="Book a room"
      description="A room that needs approval is held for you until somebody answers."
      primary={
        <Button
          icon={faCheck}
          className="py-1.5"
          pending={book.isPending}
          pendingLabel="Booking…"
          disabled={form.resourceId === '' || form.purpose.trim() === ''}
          onClick={() =>
            book.mutate(
              {
                resourceId: form.resourceId,
                purpose: form.purpose.trim(),
                notes: form.notes.trim() === '' ? null : form.notes.trim(),
                startsAt: new Date(`${form.day}T${form.startTime}`).toISOString(),
                endsAt: new Date(`${form.day}T${form.endTime}`).toISOString(),
                ...(equipment ? { quantity: Math.max(1, Number(form.quantity) || 1) } : {}),
              },
              {
                onSuccess: () => {
                  setForm(blank)
                  onDone()
                },
              },
            )
          }
        >
          {chosen?.requiresApproval ? 'Request it' : 'Book it'}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field
          label="Room or equipment"
          description={
            chosen?.requiresApproval
              ? 'This room needs approval. Your request holds the slot until somebody answers.'
              : undefined
          }
        >
          <Select
            value={form.resourceId}
            onChange={(event) => setForm((f) => ({ ...f, resourceId: event.target.value }))}
          >
            <option value="">Choose…</option>
            {(resources ?? [])
              .filter((resource) => resource.isBookable)
              .map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.name}
                  {resource.capacity
                    ? resource.kind === 'equipment'
                      ? ` (${resource.capacity} in all)`
                      : ` (${resource.capacity})`
                    : ''}
                </option>
              ))}
          </Select>
        </Field>
        {equipment && (
          <Field
            label="How many"
            description={`Of ${chosen.capacity ?? 1}. Others can book the rest at the same time.`}
          >
            <Input
              type="number"
              min={1}
              max={chosen.capacity ?? 1}
              className="block max-w-32"
              value={form.quantity}
              onChange={(event) => setForm((f) => ({ ...f, quantity: event.target.value }))}
            />
          </Field>
        )}
        <Field label="What for">
          <Input
            placeholder="Youth group"
            value={form.purpose}
            onChange={(event) => setForm((f) => ({ ...f, purpose: event.target.value }))}
          />
        </Field>
        <Field label="Day">
          <Input
            type="date"
            value={form.day}
            onChange={(event) => setForm((f) => ({ ...f, day: event.target.value }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="From">
            <Input
              type="time"
              value={form.startTime}
              onChange={(event) => setForm((f) => ({ ...f, startTime: event.target.value }))}
            />
          </Field>
          <Field label="To">
            <Input
              type="time"
              value={form.endTime}
              onChange={(event) => setForm((f) => ({ ...f, endTime: event.target.value }))}
            />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea
            rows={2}
            value={form.notes}
            onChange={(event) => setForm((f) => ({ ...f, notes: event.target.value }))}
          />
        </Field>

        {/* A clash comes back as a 409 naming the booking that has the room. */}
        <ErrorNote error={book.error} />
      </div>
    </SidePanel>
  )
}

function RoomsPanel() {
  const { data: resources, isPending } = useResources()
  const access = useAccess()
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const shown = resources?.find((resource) => resource.id === shownId)

  if (isPending) return <Spinner />

  return (
    <div className="space-y-4">
      {access.has('events', 'administrator') && (
        <div className="flex justify-end max-sm:hidden">
          <PageAction
            icon={faPlus}
            label="New room or equipment"
            onClick={() => setCreating(true)}
          />
        </div>
      )}

      {!resources || resources.length === 0 ? (
        <EmptyState title="No rooms yet" hint="Add the spaces people book, and the equipment." />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {resources.map((resource) => (
              <li key={resource.id}>
                <button
                  type="button"
                  disabled={!access.has('events', 'administrator')}
                  onClick={() => {
                    setShownId(resource.id)
                    setOpenId(resource.id)
                  }}
                  className={`${listRow} flex w-full flex-wrap items-center gap-2 text-left enabled:hover:bg-surface-sunken`}
                >
                  <span className="min-w-0 flex-1 truncate text-base font-medium text-content-default">
                    {resource.name}
                  </span>
                  {resource.kind === 'equipment' ? (
                    <Chip>
                      <Icon icon={faBoxArchive} label="Equipment, how many" className="size-3" />
                      {resource.capacity ?? 1}
                    </Chip>
                  ) : (
                    resource.capacity && (
                      <Chip>
                        <Icon icon={faUsers} label="Seats" className="size-3" />
                        {resource.capacity}
                      </Chip>
                    )
                  )}
                  {resource.requiresApproval && (
                    <Chip tone="brand">
                      <Icon icon={faLock} className="size-3" />
                      Approval
                    </Chip>
                  )}
                  {!resource.isBookable && <Chip tone="warning">Not bookable</Chip>}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <RoomForm open={creating} onClose={() => setCreating(false)} title="New room or equipment" />
      <RoomPanel
        room={shown}
        open={openId !== null && shown !== undefined}
        onClose={() => setOpenId(null)}
      />
    </div>
  )
}

interface RoomDraft {
  kind: 'room' | 'equipment'
  name: string
  capacity: string
  campusId: string
  requiresApproval: boolean
  isBookable: boolean
}

const draftOf = (room?: FacilityResource): RoomDraft => ({
  kind: room?.kind ?? 'room',
  name: room?.name ?? '',
  capacity: room?.capacity ? String(room.capacity) : '',
  campusId: room?.campusId ?? '',
  requiresApproval: room?.requiresApproval ?? false,
  isBookable: room?.isBookable ?? true,
})

/** A room's settings: a new room, or (with `room`) changing one. */
function RoomForm({
  room,
  open,
  onClose,
  title,
  description,
  footer,
  children,
}: {
  room?: FacilityResource
  open: boolean
  onClose: () => void
  title: string
  description?: string
  footer?: ReactNode
  children?: ReactNode
}) {
  const create = useCreateResource()
  const update = useUpdateResource()
  const { data: campuses } = useCampuses()
  const [form, setForm] = useState<RoomDraft>(() => draftOf(room))
  const body = {
    kind: form.kind,
    name: form.name.trim(),
    capacity: form.capacity === '' ? null : Number(form.capacity),
    campusId: form.campusId === '' ? null : form.campusId,
    requiresApproval: form.requiresApproval,
    isBookable: form.isBookable,
  }
  const pending = create.isPending || update.isPending

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setForm(draftOf(room))
          onClose()
        }
      }}
      title={title}
      description={description}
      footer={footer}
      primary={
        <Button
          icon={room ? faCheck : faPlus}
          className="py-1.5"
          pending={pending}
          disabled={form.name.trim() === ''}
          onClick={() =>
            room
              ? update.mutate({ id: room.id, ...body }, { onSuccess: onClose })
              : create.mutate(body, {
                  onSuccess: () => {
                    setForm(draftOf())
                    onClose()
                  },
                })
          }
        >
          {room ? 'Save' : 'Add room'}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field
          label="Kind"
          description={
            form.kind === 'equipment'
              ? 'Booked by how many: groups can share it at once until it runs out.'
              : 'Booked whole: one booking at a time.'
          }
        >
          <Select
            value={form.kind}
            onChange={(event) =>
              setForm((f) => ({ ...f, kind: event.target.value as RoomDraft['kind'] }))
            }
          >
            <option value="room">Room</option>
            <option value="equipment">Equipment</option>
          </Select>
        </Field>
        <Field label="Name">
          <Input
            placeholder={form.kind === 'equipment' ? 'Folding chairs' : 'Main Hall'}
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
          />
        </Field>
        <Field
          label={form.kind === 'equipment' ? 'How many there are' : 'Seats'}
          description={form.kind === 'equipment' ? 'Blank means one.' : 'Optional.'}
        >
          <Input
            type="number"
            min={1}
            className="block max-w-32"
            value={form.capacity}
            onChange={(event) => setForm((f) => ({ ...f, capacity: event.target.value }))}
          />
        </Field>
        {(campuses ?? []).length > 0 && (
          <Field label="Campus" description="Blank for a shared room.">
            <Select
              value={form.campusId}
              onChange={(event) => setForm((f) => ({ ...f, campusId: event.target.value }))}
            >
              <option value="">All campuses</option>
              {(campuses ?? []).map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            checked={form.requiresApproval}
            onChange={(event) => setForm((f) => ({ ...f, requiresApproval: event.target.checked }))}
          />
          Requests for this need approving first
        </label>
        {room && (
          <label className="flex items-center gap-2 text-sm text-content-default">
            <input
              type="checkbox"
              checked={form.isBookable}
              onChange={(event) => setForm((f) => ({ ...f, isBookable: event.target.checked }))}
            />
            Can be booked
          </label>
        )}
        <ErrorNote error={create.error ?? update.error} />
      </div>
      {children}
    </SidePanel>
  )
}

function RoomPanel({
  room,
  open,
  onClose,
}: {
  room: FacilityResource | undefined
  open: boolean
  onClose: () => void
}) {
  const remove = useRemoveResource()
  if (!room) return null
  return (
    <RoomForm
      key={room.id}
      room={room}
      open={open}
      onClose={onClose}
      title={room.name}
      description={room.kind === 'equipment' ? 'Equipment' : 'Room'}
      footer={
        <ConfirmButton
          label="Retire this room"
          icon={faTrashCan}
          variant="quietDanger"
          className="-ml-4"
          title={`Retire ${room.name}?`}
          description="Only a room with no bookings can be retired."
          confirmLabel="Retire"
          onConfirm={() => remove.mutate(room.id, { onSuccess: onClose })}
        />
      }
    >
      <ErrorNote error={remove.error} />
    </RoomForm>
  )
}
